import React, { useState, useEffect, useMemo } from 'react';
import { accountsApi } from '../../utils/accountsApi';
import './ChartOfAccountsTreeView.css';

const highlight = (text, term) => {
    if (!term || !text || term.trim() === '') return text;
    try {
        const re = new RegExp(`(${term.trim()})`, 'gi');
        const parts = String(text).split(re);
        return parts.map((p, i) => re.test(p) ? <mark key={i} className="search-highlight">{p}</mark> : p);
    } catch {
        return text;
    }
};

/**
 * Recursive tree node renderer.
 * Uses server-built tree (children[] already nested).
 */
const TreeNode = ({ node, level, expandedNodes, toggleNode, searchTerm }) => {
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expandedNodes.has(node.id);

    return (
        <div className="tree-node-container">
            <div className={`tree-node level-${level}`} style={{ paddingLeft: `${level * 24}px` }}>
                <div className="tree-node-content">
                    {hasChildren ? (
                        <button
                            className="tree-toggle"
                            onClick={() => toggleNode(node.id)}
                            aria-label={isExpanded ? 'Collapse' : 'Expand'}
                        >
                            <i className={`fas fa-chevron-${isExpanded ? 'down' : 'right'}`} />
                        </button>
                    ) : (
                        <span className="tree-toggle-placeholder" />
                    )}
                    <div className="tree-node-info">
                        <span className="tree-node-code">{highlight(node.code, searchTerm)}</span>
                        <span className="tree-node-name">
                            {highlight(node.arabicName || node.name, searchTerm)}
                            {node.arabicName && node.name && (
                                <span className="tree-node-name-en"> ({highlight(node.name, searchTerm)})</span>
                            )}
                        </span>
                        <span className={`tree-node-type badge-${node.type === 'HEADER' ? 'header' : 'posting'}`}>
                            {node.type}
                        </span>
                        {node.currency && (
                            <span className="tree-node-currency">{node.currency.code}</span>
                        )}
                        <span className="tree-node-level">L{node.level}</span>
                    </div>
                    {node.entity && (
                        <span className="tree-node-entity">{node.entity.code}</span>
                    )}
                </div>
            </div>
            {hasChildren && isExpanded && (
                <div className="tree-children">
                    {node.children.map(child => (
                        <TreeNode
                            key={child.id}
                            node={child}
                            level={level + 1}
                            expandedNodes={expandedNodes}
                            toggleNode={toggleNode}
                            searchTerm={searchTerm}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

/**
 * Filter a pre-built tree for nodes matching searchTerm.
 * Returns { filtered, toExpand } where toExpand is the set of parent ids to open.
 */
function filterTree(nodes, term) {
    if (!term) return { filtered: nodes, toExpand: new Set() };
    const lower = term.toLowerCase();
    const toExpand = new Set();

    function walk(nodes) {
        const result = [];
        nodes.forEach(node => {
            const match =
                node.code.toLowerCase().includes(lower) ||
                (node.name && node.name.toLowerCase().includes(lower)) ||
                (node.arabicName && node.arabicName.toLowerCase().includes(lower));
            const filteredChildren = walk(node.children ?? []);
            if (match || filteredChildren.length > 0) {
                if (filteredChildren.length > 0) toExpand.add(node.id);
                result.push({ ...node, children: filteredChildren });
            }
        });
        return result;
    }

    return { filtered: walk(nodes), toExpand };
}

/**
 * Collect all ids of nodes that have children (for expand-all).
 */
function collectParentIds(nodes, out = new Set()) {
    nodes.forEach(n => {
        if (n.children && n.children.length > 0) {
            out.add(n.id);
            collectParentIds(n.children, out);
        }
    });
    return out;
}

/**
 * ChartOfAccountsTreeView
 *
 * Works as a standalone page at /chart-of-accounts/tree
 * AND as an embedded panel inside ChartOfAccountsPage (via the `embedded` prop).
 * When embedded, hides the outer page chrome and fetches data itself.
 */
const ChartOfAccountsTreeView = ({ embedded = false }) => {
    const [tree, setTree] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedNodes, setExpandedNodes] = useState(new Set());
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        setLoading(true);
        accountsApi.tree()
            .then(data => {
                setTree(data);
                // Auto-expand top level
                setExpandedNodes(new Set(data.map(n => n.id)));
                // Count total nodes recursively
                function countAll(nodes) {
                    return nodes.reduce((s, n) => s + 1 + countAll(n.children ?? []), 0);
                }
                setTotalCount(countAll(data));
            })
            .catch(err => setError(err.message))
            .finally(() => setLoading(false));
    }, []);

    const toggleNode = id => {
        setExpandedNodes(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const expandAll = () => setExpandedNodes(collectParentIds(tree));
    const collapseAll = () => setExpandedNodes(new Set());

    const { filtered: filteredTree, toExpand } = useMemo(
        () => filterTree(tree, searchTerm),
        [tree, searchTerm],
    );

    // Auto-expand parents of matching nodes when searching
    useEffect(() => {
        if (searchTerm && toExpand.size > 0) {
            setExpandedNodes(prev => new Set([...prev, ...toExpand]));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm]);

    if (loading) {
        return (
            <div className="chart-tree-view">
                <div className="loading-spinner">
                    <i className="fas fa-spinner fa-spin" />
                    <p>Loading Chart of Accounts…</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="chart-tree-view">
                <div className="error-message">
                    <i className="fas fa-exclamation-circle" />
                    <p>Error: {error}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="chart-tree-view">
            {!embedded && (
                <div className="tree-header">
                    <h1 className="page-title">Chart of Accounts — Tree View</h1>
                </div>
            )}

            <div className="tree-controls">
                <div className="search-bar">
                    <input
                        type="text"
                        placeholder="Search code, name, Arabic name…"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        className="search-input"
                    />
                    {searchTerm && (
                        <button className="clear-search" onClick={() => setSearchTerm('')} aria-label="Clear">×</button>
                    )}
                    <i className="fas fa-search search-icon" />
                </div>
                <div className="tree-actions">
                    <button onClick={expandAll} className="btn-expand">
                        <i className="fas fa-plus-square" /> Expand All
                    </button>
                    <button onClick={collapseAll} className="btn-collapse">
                        <i className="fas fa-minus-square" /> Collapse All
                    </button>
                </div>
            </div>

            <div className="tree-container">
                {filteredTree.length > 0 ? (
                    filteredTree.map(node => (
                        <TreeNode
                            key={node.id}
                            node={node}
                            level={0}
                            expandedNodes={expandedNodes}
                            toggleNode={toggleNode}
                            searchTerm={searchTerm}
                        />
                    ))
                ) : (
                    <div className="no-results">
                        <i className="fas fa-search" />
                        <p>No accounts match your search.</p>
                    </div>
                )}
            </div>

            <div className="tree-footer">
                <p>Total Accounts: {totalCount}</p>
                <p>Root Accounts: {tree.length}</p>
            </div>
        </div>
    );
};

export default ChartOfAccountsTreeView;
